export type ScheduleSlot = {
  startTime: string;
  endTime: string;
};

export type SchedulableTreatment = {
  key: string;
  slots: ScheduleSlot[];
};

export type ScheduledTreatment = {
  key: string;
  slot: ScheduleSlot;
};

export type TreatmentSchedule = {
  treatments: ScheduledTreatment[];
  totalGap: number;
};

type SearchResult = {
  slots: ScheduleSlot[];
  totalGap: number;
};

export type SearchBudget = { steps: number };

const MAX_REORDERABLE_TREATMENTS = 12;
const MAX_SEARCH_STEPS = 100_000;

export function toMinutes(time: string): number {
  const [hours, minutes] = time.split(':');
  return Number(hours) * 60 + Number(minutes);
}

const durationOf = (slot: ScheduleSlot) => toMinutes(slot.endTime) - toMinutes(slot.startTime);

export function slotsOverlap(a: ScheduleSlot, b: ScheduleSlot): boolean {
  return (
    toMinutes(a.startTime) < toMinutes(b.endTime) && toMinutes(b.startTime) < toMinutes(a.endTime)
  );
}

export function freeSlots<T extends ScheduleSlot>(slots: T[], occupied: ScheduleSlot[]): T[] {
  return slots.filter((slot) => !occupied.some((interval) => slotsOverlap(slot, interval)));
}

function sortSlots(slots: ScheduleSlot[]): ScheduleSlot[] {
  return [...slots].sort((a, b) => toMinutes(a.startTime) - toMinutes(b.startTime));
}

function totalGapOf(slots: ScheduleSlot[]): number {
  const start = Math.min(...slots.map((slot) => toMinutes(slot.startTime)));
  const end = Math.max(...slots.map((slot) => toMinutes(slot.endTime)));
  const busy = slots.reduce((sum, slot) => sum + durationOf(slot), 0);
  return end - start - busy;
}

function idleBetween(
  earliest: number,
  cursor: number,
  chosenDuration: number,
  fixed: ScheduleSlot[]
): number {
  const fixedInside = fixed
    .filter((slot) => toMinutes(slot.startTime) >= earliest && toMinutes(slot.endTime) <= cursor)
    .reduce((sum, slot) => sum + durationOf(slot), 0);
  return cursor - earliest - chosenDuration - fixedInside;
}

export const isOverBudget = (budget: SearchBudget) => budget.steps > MAX_SEARCH_STEPS;

function scheduleInListedOrder(
  treatments: SchedulableTreatment[],
  fixed: ScheduleSlot[],
  budget: SearchBudget
): TreatmentSchedule | null {
  const search: { best: SearchResult | null } = { best: null };

  const walk = (index: number, cursor: number, chosen: ScheduleSlot[], chosenDuration: number) => {
    if (index === treatments.length) {
      const totalGap = totalGapOf([...chosen, ...fixed]);
      if (!search.best || totalGap < search.best.totalGap) {
        search.best = { slots: [...chosen], totalGap };
      }
      return;
    }
    for (const slot of treatments[index].slots) {
      budget.steps += 1;
      if (isOverBudget(budget)) return;
      const start = toMinutes(slot.startTime);
      if (index > 0 && start < cursor) continue;
      const end = toMinutes(slot.endTime);
      const earliest = index === 0 ? start : toMinutes(chosen[0].startTime);
      const nextDuration = chosenDuration + durationOf(slot);
      const bound = idleBetween(earliest, end, nextDuration, fixed);
      if (search.best && bound >= search.best.totalGap) continue;
      chosen.push(slot);
      walk(index + 1, end, chosen, nextDuration);
      chosen.pop();
      if (search.best?.totalGap === 0) return;
    }
  };

  walk(0, 0, [], 0);

  const { best } = search;
  if (!best) return null;

  return {
    treatments: treatments.map((treatment, index) => ({
      key: treatment.key,
      slot: best.slots[index],
    })),
    totalGap: best.totalGap,
  };
}

function scheduleInBestOrder(
  treatments: SchedulableTreatment[],
  fixed: ScheduleSlot[],
  budget: SearchBudget
): TreatmentSchedule | null {
  const fixedStart = fixed.length
    ? Math.min(...fixed.map((slot) => toMinutes(slot.startTime)))
    : null;
  const fixedEnd = fixed.length ? Math.max(...fixed.map((slot) => toMinutes(slot.endTime))) : null;

  const idleWithin = (from: number, to: number) =>
    to -
    from -
    fixed
      .filter((slot) => toMinutes(slot.startTime) >= from && toMinutes(slot.endTime) <= to)
      .reduce((sum, slot) => sum + durationOf(slot), 0);

  const openingIdle = (start: number) =>
    fixedStart !== null && fixedStart < start ? idleWithin(fixedStart, start) : 0;

  const closingIdle = (cursor: number) =>
    fixedEnd !== null && fixedEnd > cursor ? idleWithin(cursor, fixedEnd) : 0;

  const cursors = Array.from(
    new Set(
      treatments.flatMap((treatment) => treatment.slots.map((slot) => toMinutes(slot.endTime)))
    )
  ).sort((a, b) => a - b);
  const cursorIndex = new Map(cursors.map((cursor, index) => [cursor, index]));
  const everyTreatment = (1 << treatments.length) - 1;
  const memo = new Float64Array((everyTreatment + 1) * cursors.length).fill(-1);

  const remainingGap = (placed: number, cursor: number): number => {
    if (placed === everyTreatment) return closingIdle(cursor);
    const memoKey = placed * cursors.length + (cursorIndex.get(cursor) ?? 0);
    if (memo[memoKey] >= 0) return memo[memoKey];
    if (isOverBudget(budget)) return Infinity;

    let best = Infinity;
    treatments.forEach((treatment, index) => {
      if (placed & (1 << index)) return;
      for (const slot of treatment.slots) {
        budget.steps += 1;
        const start = toMinutes(slot.startTime);
        if (start < cursor) continue;
        const wait = idleWithin(cursor, start);
        if (wait >= best) break;
        const total = wait + remainingGap(placed | (1 << index), toMinutes(slot.endTime));
        if (total < best) best = total;
      }
    });

    memo[memoKey] = best;
    return best;
  };

  const opening: { index: number; slot: ScheduleSlot | null; total: number } = {
    index: -1,
    slot: null,
    total: Infinity,
  };
  treatments.forEach((treatment, index) => {
    treatment.slots.forEach((slot) => {
      const start = toMinutes(slot.startTime);
      const total = openingIdle(start) + remainingGap(1 << index, toMinutes(slot.endTime));
      const startsEarlier = opening.slot !== null && start < toMinutes(opening.slot.startTime);
      if (total < opening.total || (total === opening.total && startsEarlier)) {
        opening.index = index;
        opening.slot = slot;
        opening.total = total;
      }
    });
  });

  if (isOverBudget(budget) || opening.slot === null || opening.total === Infinity) return null;

  const sequence: ScheduledTreatment[] = [
    { key: treatments[opening.index].key, slot: opening.slot },
  ];
  let placed = 1 << opening.index;
  let cursor = toMinutes(opening.slot.endTime);
  let owed = remainingGap(placed, cursor);

  while (placed !== everyTreatment) {
    let advanced = false;
    for (let index = 0; index < treatments.length && !advanced; index++) {
      if (placed & (1 << index)) continue;
      for (const slot of treatments[index].slots) {
        const start = toMinutes(slot.startTime);
        if (start < cursor) continue;
        const next = placed | (1 << index);
        const wait = idleWithin(cursor, start);
        if (wait + remainingGap(next, toMinutes(slot.endTime)) !== owed) continue;
        sequence.push({ key: treatments[index].key, slot });
        owed -= wait;
        placed = next;
        cursor = toMinutes(slot.endTime);
        advanced = true;
        break;
      }
    }
    if (!advanced) return null;
  }

  return { treatments: sequence, totalGap: opening.total };
}

function startsAt(schedule: TreatmentSchedule): number {
  return Math.min(...schedule.treatments.map((treatment) => toMinutes(treatment.slot.startTime)));
}

export function isBetterSchedule(
  candidate: TreatmentSchedule,
  current: TreatmentSchedule
): boolean {
  if (candidate.totalGap !== current.totalGap) return candidate.totalGap < current.totalGap;
  return startsAt(candidate) < startsAt(current);
}

export function buildTreatmentSchedule(
  treatments: SchedulableTreatment[],
  fixed: ScheduleSlot[] = [],
  budget: SearchBudget = { steps: 0 }
): TreatmentSchedule | null {
  if (treatments.length === 0) return null;

  const prepared = treatments.map((treatment) => ({
    ...treatment,
    slots: sortSlots(freeSlots(treatment.slots, fixed)),
  }));
  if (prepared.some((treatment) => treatment.slots.length === 0)) return null;

  const listed = scheduleInListedOrder(prepared, fixed, budget);
  if (listed?.totalGap === 0) return listed;
  if (prepared.length > MAX_REORDERABLE_TREATMENTS || isOverBudget(budget)) return listed;

  const optimal = scheduleInBestOrder(prepared, fixed, budget);
  if (!optimal) return listed;
  return !listed || isBetterSchedule(optimal, listed) ? optimal : listed;
}
